/**
 * Queue Firestore run name updates when the device is offline (or updateDoc failed).
 * Flushed on online / background sync (see usePendingRunsAutoSync).
 */

import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

const QUEUE_KEY = 'pendingRunMetadataUpdates_v1';

function readQueueRaw() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(list) {
  try {
    if (!list.length) localStorage.removeItem(QUEUE_KEY);
    else localStorage.setItem(QUEUE_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('[pendingRunMetadataQueue] write failed:', e);
  }
}

/**
 * @param {string} runId Firestore document id
 * @param {{ runName: string, name?: string }} fields trimmed display name (both runName and name kept in sync)
 */
export function enqueuePendingRunMetadataUpdate(runId, { runName, name }) {
  if (!runId || typeof runId !== 'string') return;
  if (runId.startsWith('local-') || runId.startsWith('queued-inline-')) return;
  const t = typeof runName === 'string' ? runName.trim() : '';
  if (!t) return;
  const display = typeof name === 'string' && name.trim() ? name.trim() : t;
  const list = readQueueRaw().filter((x) => x?.runId !== runId);
  list.push({ runId, runName: t, name: display, enqueuedAt: Date.now() });
  writeQueue(list);
}

export function readPendingRunMetadataQueue() {
  return readQueueRaw();
}

/** Remove queued metadata patch for a run (e.g. after a successful direct updateDoc). */
export function clearPendingRunMetadataUpdateForRun(runId) {
  if (!runId) return;
  const next = readQueueRaw().filter((x) => x?.runId !== runId);
  writeQueue(next);
}

function removeFromQueue(runId) {
  const next = readQueueRaw().filter((x) => x?.runId !== runId);
  writeQueue(next);
}

/**
 * Push pending name updates to Firestore. Stops at first failure so items stay queued.
 * @param {import('firebase/firestore').Firestore} db
 */
export async function flushPendingRunMetadataUpdates(db) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  if (!db) return;

  const queue = readQueueRaw();
  if (queue.length === 0) return;

  for (const item of queue) {
    const runId = item?.runId;
    const runName = typeof item?.runName === 'string' ? item.runName.trim() : '';
    const name = typeof item?.name === 'string' && item.name.trim() ? item.name.trim() : runName;
    if (!runId || !runName) {
      removeFromQueue(runId);
      continue;
    }
    try {
      await updateDoc(doc(db, 'runs', runId), {
        runName,
        name,
        updatedAt: serverTimestamp(),
      });
      removeFromQueue(runId);
    } catch (err) {
      console.warn('[pendingRunMetadataQueue] flush failed for', runId, err?.code || err?.message);
      break;
    }
  }
}
